import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "@lokalise/xlsx";
import { config } from "../../config/config";
import Popover from "@mui/material/Popover";
import Loader from "../Loader/Loader";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useUserAuthentication } from "../../utils/utils";
import { useHistory } from "react-router-dom";

let api = config.api.api_url;

const FileUpload = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [filterData, setFilterData] = useState("");
  const open = Boolean(anchorEl);
  const { login } = useUserAuthentication();
  let history = useHistory();
  const URL = history?.location?.pathname;
  const Use_Case_Name = URL.split("/").pop();

  const isValidExtension = (fileName) => {
    const allowedExtensions = [".xlsx", ".xls", ".csv"];
    return allowedExtensions.some((ext) =>
      fileName.toLowerCase().endsWith(ext),
    );
  };

  const isValidStructure = (columns) => {
    return Object.keys(props.requiredColumns).every((col) =>
      columns.includes(col),
    );
  };

  const processFile = (file) => {
    props.updateSourceInputs("is_initial", false);
    const isValidFileType = isValidExtension(file.name);
    if (!isValidFileType) {
      props.updateAlert({
        open: true,
        message: "Invalid file format. Please upload an Excel or CSV file.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (json.length === 0) {
          props.updateAlert({
            open: true,
            message: "The uploaded file is empty.",
            severity: "Failed",
            closeFlag: true,
          });
          props.updateSourceInputs("is_initial", true);
          return;
        }

        const fileColumns = Object.keys(json[0]);
        if (!isValidStructure(fileColumns)) {
          props.updateAlert({
            open: true,
            message:
              "Invalid file structure. Required columns are missing, Please check sample data and try again",
            severity: "Failed",
            closeFlag: true,
          });
          props.updateSourceInputs("is_initial", true);
          return;
        }

        props.handleFileUpload(json);
      } catch (err) {
        props.updateAlert({
          open: true,
          message: "Error reading file. Please try again.",
          severity: "Failed",
          closeFlag: true,
        });
        props.updateSourceInputs("is_initial", true);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  }, []);

  const onDropRejected = useCallback((fileRejections) => {
    props.updateAlert({
      open: true,
      message: "Invalid file format. Please upload an Excel or CSV file.",
      severity: "Failed",
      closeFlag: true,
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: false,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
    disabled: props.isFileUploading,
  });

  // OPENS DOWNLOAD DIALOG
  const openDownloadDialog = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setFilterData("");
    const anchor = event.currentTarget;
    setAnchorEl(anchor);
  };

  // DOWNLOAD WITHOUT DATA
  const downloadSample = () => {
    const MetaData_Filter = props?.source_Data?.data?.dropdown?.metadata_filter;
    const tableData = props?.source_Data?.current_mapped_data || [];

    // const tableData = props?.source_Data?.download_sample_file ?? [];

    if (!tableData.length) {
      props.updateAlert({
        open: true,
        message: "No sample data available to download.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    const order_check_data = [
      ...(props?.source_Data?.data?.dropdown?.metadata_filter || []),
      ...(props?.source_Data?.data?.dropdown?.table_header_column_list.map(
        (item) => item.name,
      ) || []),
    ];

    // Build a single header row so Excel treats each header as one cell.
    const worksheet = XLSX.utils.aoa_to_sheet([order_check_data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(
      workbook,
      `${tableData?.[0]?.rowData?.[MetaData_Filter?.[1]] || "SampleData"}_${
        props?.source_Data?.current_source || "Default"
      }.xlsx`,
    );
  };

  // DEFAULT FUNCTION FOR DOWNLOAD WITH DATA
  const handleDownloadWithData = () => {
    const MetaData_Filter = props?.source_Data?.data?.dropdown?.metadata_filter;
    const tableData =
      props?.source_Data?.current_mapped_data.map((item) => ({
        ...item.rowData,
      })) || [];

    if (!tableData.length) {
      props.updateAlert({
        open: true,
        message: "No sample data available to download.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    let order_check_data = [
      ...(MetaData_Filter || []),
      ...(props?.source_Data?.data?.dropdown?.table_header_column_list.map(
        (item) => item.name,
      ) || []),
    ];

    if (props?.source_Data?.current_source?.toLowerCase() === "nielsen") {
      order_check_data = [
        ...order_check_data.filter((item) => item !== "COUNTRY_CODE"),
        "COUNTRY_CODE",
      ];
    }

    const Download_Data = tableData.map((item) => {
      const { CREATED_AT, CREATED_BY, UPDATED_AT, UPDATED_BY, ...rest } = item;
      // filter arrayTwo keeping order of arrayOne
      let result = {};
      order_check_data.forEach((key) => {
        result = {
          ...result,
          [key]: rest[key] || "",
        };
      });
      return result;
    });

    const sampleData = [...Download_Data];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(
      workbook,
      `${tableData?.[0]?.[MetaData_Filter?.[1]] || "SampleData"}_${
        props?.source_Data?.current_source || "Default"
      }.xlsx`,
    );
  };

  // DOWNLOAD WITH DATA FUNCTION FOR ATTRIBUTE ENRICHMENT
  const handleDownloadWithDataForAE = () => {
    const MetaData_Filter = props?.source_Data?.data?.dropdown?.metadata_filter;
    const IS_SPREADABLE_DATA =
      props?.source_Data.data.dropdown.conversion_column_details.is_spread;
    const Convertion_Column_Details =
      props?.source_Data.data.dropdown.conversion_column_details
        .conversion_columns;
    const COUNTRY = props?.source_Data.current_mapped_country.find((item) => {
      return item.label === props?.source_Data.current_country;
    });
    let Data =
      props?.source_Data?.current_mapped_data?.map((item) => ({
        ...item.rowData,
        COUNTRY: COUNTRY?.value || "",
        SOURCE_FILE_ID: props?.source_Data?.current_category || "",
        IS_AI_RECOMMENDED: item?.isAiEnriched || false,
      })) || [];

    if (!Data.length) {
      props.updateAlert({
        open: true,
        message: "No sample data available to download.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    if (IS_SPREADABLE_DATA) {
      Data =
        Data?.map((item) => ({
          ...item,
          COUNTRY: COUNTRY?.value || "",
          SOURCE_FILE_ID: props?.source_Data?.current_category || "",
        })) || [];
    } else {
      Data =
        Data?.map((item) => ({
          ...item,
          COUNTRY: COUNTRY?.value || "",
          SOURCE_FILE_ID: props?.source_Data?.current_category || "",
          IS_AI_RECOMMENDED: item?.isAiEnriched || false,
        })) || [];
    }

    let tableData = [];
    if (IS_SPREADABLE_DATA) {
      tableData =
        Data?.map((item) => {
          let Spreadable_Columns = [];
          let All_Grouped_Spreaded_columns = {};
          let Spreadable_Column_Removed = {};

          // GET THE MATCHING SPREADABLE COLUMN DETAILS
          Object.keys(item).forEach((col_name) => {
            const matched = Convertion_Column_Details.filter((con_column) =>
              con_column.child_columns_name?.some(
                (child_col) => child_col === col_name,
              ),
            );

            // accumulate all matches
            if (matched.length > 0) {
              Spreadable_Columns.push(...matched);
            }
          });

          // GROUP THE SPREDABLE COLUMNS TO ITS PARENT COLUMN
          if (Spreadable_Columns.length > 0) {
            Spreadable_Columns.forEach((spread_col) => {
              Object.keys(item).forEach((key) => {
                if (spread_col.child_columns_name.includes(key)) {
                  All_Grouped_Spreaded_columns = {
                    ...All_Grouped_Spreaded_columns,
                    [spread_col.parent_column_name]: {
                      ...All_Grouped_Spreaded_columns?.[
                        spread_col.parent_column_name
                      ],
                      [key]: item?.[key],
                    },
                  };
                }
              });
            });
          } else {
            All_Grouped_Spreaded_columns = {};
          }

          // REMOVE'S THE SPREADED COLUMN FROM THE ROW DATA
          Object.keys(item).forEach((col_name) => {
            const isSpreadableColumn = Spreadable_Columns.some((spread_col) =>
              spread_col.child_columns_name.includes(col_name),
            );

            if (!isSpreadableColumn) {
              Spreadable_Column_Removed = {
                ...Spreadable_Column_Removed,
                [col_name]: item?.[col_name],
              };
            }
          });

          const FINAL = {
            ...Spreadable_Column_Removed,
            ...All_Grouped_Spreaded_columns,
          };
          return FINAL;
        }) ?? [];
    } else {
      tableData = Array.isArray(Data)
        ? Data.map((item) => {
            const columns =
              props?.source_Data?.data?.dropdown?.edit_model_column_list ?? [];

            const Final_Columns = [
              ...columns,
              { label: "IS_AI_RECOMMENDED", name: "IS_AI_RECOMMENDED" },
            ];

            return Final_Columns.reduce((acc, col) => {
              acc[col.name] = item?.[col.name] ?? null;
              return acc;
            }, {});
          })
        : [];
    }

    let order_check_data = [];
    if (IS_SPREADABLE_DATA) {
      const FINAL_ORDER_CHECK_DATA = [
        ...(MetaData_Filter || []),
        ...(props?.source_Data?.data?.dropdown?.edit_model_column_list.map(
          (item) => item.name,
        ) || []),
      ];
      order_check_data = FINAL_ORDER_CHECK_DATA;
    } else {
      const FINAL_ORDER_CHECK_DATA = [
        ...(MetaData_Filter || []),
        ...(props?.source_Data?.data?.dropdown?.edit_model_column_list.map(
          (item) => item.name,
        ) || []),
        "IS_AI_RECOMMENDED",
      ];
      order_check_data = FINAL_ORDER_CHECK_DATA;
    }

    let Sorted_Data = [];
    if (IS_SPREADABLE_DATA) {
      Sorted_Data = tableData;
    } else {
      const FINAL_SORTED_DATA = tableData.map((item) => {
        const { UPDATED_AT, UPDATED_BY, ...rest } = item;
        // filter arrayTwo keeping order of arrayOne
        let result = {};
        order_check_data.forEach((key) => {
          result = {
            ...result,
            [key]: rest[key] || "",
          };
        });
        return result;
      });
      Sorted_Data = FINAL_SORTED_DATA;
    }

    const normalizeExportValue = (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    };

    const Download_Data = Sorted_Data.map((item) => {
      const normalizedItem = Object.entries(item).reduce(
        (acc, [key, value]) => {
          acc[key] = normalizeExportValue(value);
          return acc;
        },
        {},
      );

      return {
        ...normalizedItem,
        [MetaData_Filter[1]]: props?.source_Data?.current_category || "",
        [MetaData_Filter[0]]: COUNTRY?.value || "",
      };
    });

    const sampleData = [...Download_Data];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(
      workbook,
      `${Download_Data?.[0]?.[MetaData_Filter?.[1]] || "SampleData"}_${
        props?.source_Data?.current_source || "Default"
      }.xlsx`,
    );
  };

  // DOWNLOAD WITH DATA FUNCTION FOR PRODUCT MAPPING
  const handleDownloadWithDataForProductMapping = () => {
    const MetaData_Filter = props?.source_Data?.data?.dropdown?.metadata_filter;
    const tableData =
      props?.source_Data?.current_mapped_data.map((item) => {
        return {
          ...item.rowData,
          ISNEW_LOCAL_VALUE: item?.rowData?.ISNEW_LOCAL_VALUE
            ? item?.rowData?.ISNEW_LOCAL_VALUE
            : item?.isNewLocalValue || false,
          ISNEW_GLOBAL_VALUE: item?.rowData?.ISNEW_GLOBAL_VALUE
            ? item?.rowData?.ISNEW_GLOBAL_VALUE
            : item?.isNewMappedValue || false,
        };
      }) || [];

    if (!tableData.length) {
      props.updateAlert({
        open: true,
        message: "No sample data available to download.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    let order_check_data = [
      ...(MetaData_Filter || []),
      ...(props?.source_Data?.data?.dropdown?.table_header_column_list.map(
        (item) => item.name,
      ) || []),
      "ISNEW_LOCAL_VALUE",
      "ISNEW_GLOBAL_VALUE",
    ];

    const Download_Data = tableData.map((item) => {
      const { CREATED_AT, CREATED_BY, UPDATED_AT, UPDATED_BY, ...rest } = item;
      let result = {};
      order_check_data.forEach((key) => {
        if (key === "ISNEW_LOCAL_VALUE" || key === "ISNEW_GLOBAL_VALUE") {
          result = {
            ...result,
            [key]: item?.[key] ? true : false,
          };
        }
        result = {
          ...result,
          [key]: rest[key] || "",
        };
      });
      return result;
    });

    const sampleData = [...Download_Data];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(
      workbook,
      `${tableData?.[0]?.[MetaData_Filter?.[1]] || "SampleData"}_${
        props?.source_Data?.current_source || "Default"
      }.xlsx`,
    );
  };

  // HANDLE WITH DATA, WITHOUT DATA RADIO CHANGE
  const handleRadioChange = (e) => {
    e.stopPropagation();
    setFilterData(e.target.value);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
    setFilterData("");
  };

  // Handle side effects when filterData changes
  useEffect(() => {
    if (filterData === "With Data") {
      if (Use_Case_Name === "attribute_enrichment") {
        handleDownloadWithDataForAE();
      } else if (Use_Case_Name === "attribute_mapping") {
        handleDownloadWithDataForProductMapping();
      } else {
        handleDownloadWithData();
      }
    } else if (filterData === "Without Data") {
      downloadSample();
    }
  }, [filterData]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginLeft: "-30px",
      }}
    >
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? "active" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <span className="material-symbols-outlined upload-icon">upload</span>
          <p className="upload-text">
            {props.isFileUploading
              ? "Uploading..."
              : "Drag & drop or click to upload a file"}
          </p>
          {props?.source_Data?.data?.dropdown
            ?.is_sample_download_file_available && (
            <>
              <span>|</span>
              <button onClick={openDownloadDialog} className="sample-button">
                Download Template
              </button>
            </>
          )}
          <Popover
            id={"reusable-template"}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <div
              className="reuse-popover-container"
              onClick={(e) => e.stopPropagation()}
              style={{ paddingBottom: "15px" }}
            >
              {/* WITH DATA, WITHOUT DATA FILTER */}
              <div>
                <div
                  className="mg-model-header-item"
                  style={{ height: "25px" }}
                >
                  Choose Filter:{" "}
                  <p
                    style={{
                      display: "inline",
                      color: "red",
                      fontSize: 16,
                    }}
                  >
                    *
                  </p>
                </div>
                <div
                  className="radio-input-container"
                  style={{ marginBottom: "15px" }}
                >
                  {/* WITH DATA */}
                  <div className="radio-input-div">
                    <input
                      id="withdata-checkbox"
                      type="radio"
                      value="With Data"
                      checked={filterData === "With Data"}
                      onChange={handleRadioChange}
                      className="radio-checkbox-label"
                    />
                    <label
                      htmlFor="withdata-checkbox"
                      style={{ cursor: "pointer" }}
                    >
                      With Data
                    </label>
                  </div>

                  {/* WITHOUT DATA */}
                  <div className="radio-input-div">
                    <input
                      id="withoutdata-checkbox"
                      type="radio"
                      value="Without Data"
                      checked={filterData === "Without Data"}
                      onChange={handleRadioChange}
                      className="radio-checkbox-label"
                    />
                    <label
                      htmlFor="withoutdata-checkbox"
                      style={{ cursor: "pointer" }}
                    >
                      Without Data
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
